'use client';

import { c as _c } from "react/compiler-runtime";
import { type ChainId, ChainUtils } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { AvatarMultichain } from '@morpho-org/ui-v6/client';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Badge, Button, Icon, Separator, Tooltip, TooltipContent, TooltipTrigger, useHasTouchInput } from '@repo/ui';
import { ArrowDiagonalUp, RewardsGradient } from '@repo/ui/icons';
import { useState } from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger, Popover, PopoverContent, PopoverTrigger } from '@/components/common/overlays';

/**
 * Structural shape shared by `ApiMarketCollateralReward` (markets list) and
 * the `market.apy` collateral rewards (market page). Display-only reward
 * APR earned for holding the collateral asset.
 */
export interface CollateralRewardEntry {
  asset: {
    chainId: number;
    address: string;
    symbol: string;
    logoURI?: string | null;
  };
  collateralApr: number;
}
export interface CollateralYieldTagsProps {
  /** Native yield as a fraction (e.g. `0.0233` → `2.33%`). */
  nativeYield?: number | null;
  /** Asset-level reward APRs earned for holding the collateral. */
  collateralRewards?: CollateralRewardEntry[] | null;
}

// Reward rate is shown like native yield — a plain percentage, no leading sign.
const percentFormatter = formatters.percent.digits(2).unit('%');
const rewardRowClassName = 'flex min-h-8 items-center justify-between';
const CollateralRewardsTag = t0 => {
  const $ = _c(25);
  const {
    rewards
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const hasTouchInput = useHasTouchInput();
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== rewards) {
    const totalRewardApr = rewards.reduce(_temp, 0);
    T0 = Badge;
    t1 = "tertiary";
    t2 = "sm";
    t3 = "cursor-default";
    t4 = percentFormatter.of(totalRewardApr);
    $[0] = rewards;
    $[1] = T0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    T0 = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <RewardsGradient />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== T0 || $[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t6 = <T0 variant={t1} size={t2} className={t3}>{t4}{t5}</T0>;
    $[7] = T0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const rewardTag = t6;
  let t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex min-h-8 items-center"><span className="text-foreground text-sm leading-none">Collateral Rewards</span></div>;
    t8 = <p className="text-2xs text-secondary-foreground">Rewards on collateral deposited in this market.</p>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="my-1.5"><Separator /></div>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== rewards) {
    t10 = rewards.map(_temp2);
    $[16] = rewards;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = <div className="px-sm py-sm">{t7}{t8}{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const rewardsContent = t11;
  let t12;
  if ($[20] !== hasTouchInput || $[21] !== isOpen || $[22] !== rewardTag || $[23] !== rewardsContent) {
    t12 = hasTouchInput ? <Popover open={isOpen} onOpenChange={setIsOpen}><PopoverTrigger asChild={true} onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(_temp3);
      }}>{rewardTag}</PopoverTrigger><PopoverContent side="top" sideOffset={8} style={{
        padding: 0,
        outline: "none",
        minWidth: 260,
        maxWidth: 315
      }} onClick={_temp4}>{rewardsContent}</PopoverContent></Popover> : <HoverCard openDelay={200} closeDelay={100}><HoverCardTrigger asChild={true}>{rewardTag}</HoverCardTrigger><HoverCardContent style={{
        minWidth: "260px",
        maxWidth: "315px"
      }} side="top" sideOffset={8} onClick={_temp5}>{rewardsContent}</HoverCardContent></HoverCard>;
    $[20] = hasTouchInput;
    $[21] = isOpen;
    $[22] = rewardTag;
    $[23] = rewardsContent;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
};

/**
 * Collateral yield treatment, consistent across every surface that shows a
 * market's collateral asset (markets table, market page, vault page).
 *
 * Renders the collateral's native yield and, when an active rewards program
 * exists, its reward rate — both asset-level and explicitly framed as not
 * affecting the borrow rate. Renders nothing when neither is available, so
 * markets without yield or rewards stay clean (no zero/empty state).
 */
const CollateralYieldTags = t0 => {
  const $ = _c(11);
  const {
    nativeYield,
    collateralRewards
  } = t0;
  const hasNativeYield = nativeYield != null && nativeYield !== 0;
  let t1;
  if ($[0] !== collateralRewards) {
    t1 = collateralRewards?.filter(_temp6) ?? [];
    $[0] = collateralRewards;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rewards = t1;
  const hasRewards = rewards.length > 0;
  if (!hasNativeYield && !hasRewards) {
    return null;
  }
  let t2;
  if ($[2] !== hasNativeYield || $[3] !== nativeYield) {
    t2 = hasNativeYield && <Tooltip><TooltipTrigger asChild={true}><Badge variant="tertiary" size="sm" className="cursor-default">{percentFormatter.of(nativeYield)}</Badge></TooltipTrigger><TooltipContent>Native yield on the collateral asset. It applies to the collateral only and doesn't affect the borrow rate.</TooltipContent></Tooltip>;
    $[2] = hasNativeYield;
    $[3] = nativeYield;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== hasRewards || $[6] !== rewards) {
    t3 = hasRewards && <CollateralRewardsTag rewards={rewards} />;
    $[5] = hasRewards;
    $[6] = rewards;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
export default CollateralYieldTags;
function _temp(sum, reward) {
  return sum + reward.collateralApr;
}
function _temp2(reward_0) {
  const chainId = reward_0.asset.chainId as ChainId;
  const rewardChain = InterfaceChainUtils.tryGetChain(chainId) ?? null;
  return <div key={reward_0.asset.address} className={rewardRowClassName}><div className="gap-2xs flex items-center leading-none"><AvatarMultichain src={reward_0.asset.logoURI ?? null} chain={rewardChain ? {
        id: chainId,
        name: rewardChain.name
      } : null} size="medium" /><span className="text-2xs text-secondary-foreground">{reward_0.asset.symbol}</span>{rewardChain && <ExternalLink href={ChainUtils.getExplorerAddressUrl(chainId, reward_0.asset.address)}><Button variant="ghost" size="iconSm" aria-label="View on explorer"><ArrowDiagonalUp /></Button></ExternalLink>}</div><div className="gap-2xs flex items-center leading-none"><Icon icon={<RewardsGradient />} /><span className="text-2xs text-secondary-foreground">{percentFormatter.of(reward_0.collateralApr)}</span></div></div>;
}
function _temp3(prev) {
  return !prev;
}
function _temp4(e_0) {
  return e_0.stopPropagation();
}
function _temp5(e_1) {
  return e_1.stopPropagation();
}
function _temp6(reward) {
  return reward.collateralApr !== 0;
}